import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useParams } from "react-router";
import { useSelector } from "react-redux";
import { ArrowLeft, Download, Inbox } from "lucide-react";
import PageShell from "../../components/dashboard/PageShell";
import DataTable from "../../components/dashboard/DataTable";
import ListPagination from "../../components/dashboard/ListPagination";
import Button from "../../components/ui/Button";
import { USE_MOCK_AUTH } from "../../config/api";
import { getProviderReport } from "../../config/providerReports";
import { fetchProviderReport } from "../../services/reportsApi";
import { exportReport } from "../../utils/exportReport";
import { showError } from "../../utils/toast";
import { splitPersonName } from "../../utils/personName";

const MISSING = "not worked on";

function withPersonColumns(columns, rows) {
  const named = columns.some((column) => column.key === "customer" || column.key === "customer_name");
  if (!named) return { columns, rows };
  return {
    columns: columns.flatMap((column) => {
      if (column.key !== "customer" && column.key !== "customer_name") return [column];
      return [
        { ...column, key: "first_name", label: "First name" },
        { ...column, key: "last_name", label: "Last name" },
      ];
    }),
    rows: rows.map((row) => {
      const parts = splitPersonName(row);
      return {
        ...row,
        first_name: parts.first === "—" ? null : parts.first,
        last_name: parts.last === "—" ? null : parts.last,
      };
    }),
  };
}

const EMPTY_FILTERS = {
  date_from: "",
  date_to: "",
  batch_id: "",
  case_status: "",
  ageing_bucket: "",
  agent: "",
  payment_status: "",
  payment_type: "",
  payment_channel: "",
  processing_status: "",
  reconciliation_status: "",
  escalation_status: "",
  period: "monthly",
  search: "",
};

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[10px] font-bold uppercase tracking-[0.08em] text-primary/40">
        {label}
      </span>
      {children}
    </label>
  );
}

function inputClass() {
  return "w-full rounded-lg border border-primary/10 bg-white px-2.5 py-2 text-xs text-primary";
}

function Select({ value, onChange, options, placeholder = "All" }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} className={inputClass()}>
      <option value="">{placeholder}</option>
      {options.map((option) => {
        const id = typeof option === "object" ? option.id : option;
        const name = typeof option === "object" ? option.name : option;
        return (
          <option key={id} value={id}>
            {name}
          </option>
        );
      })}
    </select>
  );
}

function AgeingBrackets({ value }) {
  const parts = String(value)
    .split(/\n|·/)
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.length < 2) return formatCell(value);
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      {parts.map((part) => {
        const splitAt = part.lastIndexOf(":");
        const label = splitAt >= 0 ? part.slice(0, splitAt).trim() : part;
        const count = splitAt >= 0 ? part.slice(splitAt + 1).trim() : "";
        return (
          <div key={part} className="rounded-lg border border-primary/10 bg-white px-3 py-2">
            <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-primary/45">{label}</p>
            <p className="mt-1 text-sm font-semibold tabular-nums text-primary">{count}</p>
          </div>
        );
      })}
    </div>
  );
}

function formatCell(value) {
  if (value == null || value === "") return "—";
  if (typeof value === "string" && (value.includes("T") || /^\d{4}-\d{2}-\d{2}/.test(value))) {
    const date = new Date(value);
    if (!Number.isNaN(date.getTime()) && value.length >= 10) {
      return value.includes("T")
        ? date.toLocaleString(undefined, {
            year: "numeric",
            month: "short",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
          })
        : date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "2-digit" });
    }
  }
  return String(value);
}

export default function ReportDetailPage() {
  const { slug } = useParams();
  const report = getProviderReport(slug);
  const token = useSelector((state) => state.auth.token);
  const useLiveApi = Boolean(token) && !USE_MOCK_AUTH;
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [applied, setApplied] = useState(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [options, setOptions] = useState({});
  const [pagination, setPagination] = useState({ total: 0, last_page: 1, from: null, to: null });
  const [loading, setLoading] = useState(true);
  const requestRef = useRef(0);

  const setFilter = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));
  const enabled = report?.filters || [];

  const load = useCallback(
    async (nextPage = 1) => {
      if (!useLiveApi || !report) {
        setRows([]);
        setLoading(false);
        return;
      }
      const requestId = ++requestRef.current;
      setPage(nextPage);
      setRows([]);
      setColumns([]);
      setLoading(true);
      try {
        const data = await fetchProviderReport(token, report.slug, {
          ...applied,
          page: nextPage,
          limit: 25,
        });
        if (requestId !== requestRef.current) return;
        const nextColumns = (Array.isArray(data.columns) ? data.columns : []).filter(
          (column) => column.available !== false
        );
        const primaryKey = nextColumns[0]?.key;
        let nextRows = Array.isArray(data.rows) ? data.rows : [];
        if (primaryKey) {
          nextRows = nextRows.filter(
            (row) => row && Object.prototype.hasOwnProperty.call(row, primaryKey)
          );
        }
        if (report.slug === "outstanding-debt-ageing") {
          nextRows = nextRows.filter((row) => {
            const batch = String(row.batch_id ?? "").trim();
            const provider = String(row.provider_id ?? "").trim();
            const batchOk = batch === "" || batch === "—" || /^\d+$/.test(batch);
            const providerOk =
              provider === "" || provider === "—" || (/^\d+$/.test(provider) && provider.length < 8);
            return batchOk && providerOk;
          });
        }
        if (report.slug === "revenue") {
          nextRows = nextRows.filter((row) => {
            const period = String(row.reporting_period || "");
            return /^\d{4}-\d{2}(-\d{2})?$/.test(period) || period.includes(" to ");
          });
        }
        const shaped = withPersonColumns(nextColumns, nextRows);
        setColumns(shaped.columns);
        setRows(shaped.rows);
        setOptions(data.filter_options || {});
        setPagination({
          total: Number(data.pagination?.total || 0),
          last_page: Number(data.pagination?.last_page || 1),
          current_page: Number(data.pagination?.current_page || nextPage),
          from: data.pagination?.from ?? null,
          to: data.pagination?.to ?? null,
        });
        setPage(Number(data.pagination?.current_page || nextPage));
      } catch (error) {
        if (requestId !== requestRef.current) return;
        showError(error.message || "Could not load report.");
      } finally {
        if (requestId === requestRef.current) setLoading(false);
      }
    },
    [applied, report, token, useLiveApi]
  );

  useEffect(() => {
    load(1);
  }, [load]);

  const applyFilters = (event) => {
    event?.preventDefault?.();
    setPage(1);
    setApplied({ ...filters });
  };

  const resetFilters = () => {
    setFilters(EMPTY_FILTERS);
    setApplied(EMPTY_FILTERS);
    setPage(1);
  };

  const handleExport = async (format) => {
    let exportColumns = columns;
    let exportRows = rows;
    if (useLiveApi && report) {
      try {
        const data = await fetchProviderReport(token, report.slug, { ...applied, page: 1, limit: 5000 });
        if (Array.isArray(data.columns) && data.columns.length) exportColumns = data.columns;
        if (Array.isArray(data.rows)) exportRows = data.rows;
      } catch (error) {
        showError(error.message || "Could not export report.");
        return;
      }
    }
    const sheetRows = exportRows.map((row) => {
      const out = {};
      exportColumns.forEach((column) => {
        out[column.label || column.key] = row[column.key] ?? "";
      });
      return out;
    });
    const payloadRows = sheetRows.length
      ? sheetRows
      : [
          Object.fromEntries(
            (exportColumns.length ? exportColumns : [{ label: "Message", key: "message" }]).map((column) => [
              column.label || column.key,
              exportColumns.length ? "" : "No rows",
            ])
          ),
        ];
    exportReport(
      format,
      {
        title: report.name,
        type: "Provider report",
        period:
          applied.date_from || applied.date_to
            ? `${applied.date_from || "…"} to ${applied.date_to || "…"}`
            : "All dates",
        report: { Rows: payloadRows },
      },
      report.slug
    );
  };

  const activeFilterCount = useMemo(
    () =>
      Object.entries(applied).filter(([key, value]) => {
        if (key === "period" && value === "monthly") return false;
        return value !== "";
      }).length,
    [applied]
  );

  if (!report) {
    return <Navigate to="/dashboard/reports" replace />;
  }

  return (
    <PageShell title={report.name} description={report.purpose}>
      <div className="mb-4">
        <Link
          to="/dashboard/reports"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary/55 hover:text-primary"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          All reports
        </Link>
      </div>

      <p className="mb-4 rounded-xl border border-primary/[0.07] bg-primary/[0.03] px-4 py-3 text-sm text-primary/65">
        {report.purpose}
      </p>

      <form
        onSubmit={applyFilters}
        className="mb-4 rounded-xl border border-primary/[0.07] bg-white p-4 shadow-[0_1px_2px_rgba(127,29,29,0.04)]"
      >
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Field label="From">
            <input
              type="date"
              value={filters.date_from}
              onChange={(e) => setFilter("date_from", e.target.value)}
              className={inputClass()}
            />
          </Field>
          <Field label="To">
            <input
              type="date"
              value={filters.date_to}
              onChange={(e) => setFilter("date_to", e.target.value)}
              className={inputClass()}
            />
          </Field>
          {enabled.includes("period") ? (
            <Field label="Period">
              <select
                value={filters.period || "monthly"}
                onChange={(e) => setFilter("period", e.target.value || "monthly")}
                className={inputClass()}
              >
                {(options.period_grains || ["daily", "weekly", "monthly"]).map((grain) => (
                  <option key={grain} value={grain}>
                    {grain.charAt(0).toUpperCase() + grain.slice(1)}
                  </option>
                ))}
              </select>
            </Field>
          ) : null}
          {enabled.includes("batch_id") ? (
            <Field label="Batch">
              <Select
                value={filters.batch_id}
                onChange={(value) => setFilter("batch_id", value)}
                options={options.batches || []}
              />
            </Field>
          ) : null}
          {enabled.includes("case_status") ? (
            <Field label="Case status">
              <Select
                value={filters.case_status}
                onChange={(value) => setFilter("case_status", value)}
                options={options.case_statuses || []}
              />
            </Field>
          ) : null}
          {enabled.includes("ageing_bucket") ? (
            <Field label="Ageing bucket">
              <Select
                value={filters.ageing_bucket}
                onChange={(value) => setFilter("ageing_bucket", value)}
                options={options.ageing_buckets || ["0–30", "31–60", "61–90", "90+"]}
              />
            </Field>
          ) : null}
          {enabled.includes("agent") ? (
            <Field label="Agent">
              <Select
                value={filters.agent}
                onChange={(value) => setFilter("agent", value)}
                options={options.agents || []}
              />
            </Field>
          ) : null}
          {enabled.includes("payment_status") ? (
            <Field label="Payment status">
              <Select
                value={filters.payment_status}
                onChange={(value) => setFilter("payment_status", value)}
                options={options.payment_statuses || []}
              />
            </Field>
          ) : null}
          {enabled.includes("payment_type") ? (
            <Field label="Payment type">
              <Select
                value={filters.payment_type}
                onChange={(value) => setFilter("payment_type", value)}
                options={options.payment_types || []}
              />
            </Field>
          ) : null}
          {enabled.includes("payment_channel") ? (
            <Field label="Payment channel">
              <Select
                value={filters.payment_channel}
                onChange={(value) => setFilter("payment_channel", value)}
                options={options.payment_channels || []}
              />
            </Field>
          ) : null}
          {enabled.includes("processing_status") ? (
            <Field label="Processing status">
              <Select
                value={filters.processing_status}
                onChange={(value) => setFilter("processing_status", value)}
                options={options.processing_statuses || []}
              />
            </Field>
          ) : null}
          {enabled.includes("reconciliation_status") ? (
            <Field label="Reconciliation status">
              <Select
                value={filters.reconciliation_status}
                onChange={(value) => setFilter("reconciliation_status", value)}
                options={options.reconciliation_statuses || []}
              />
            </Field>
          ) : null}
          {enabled.includes("escalation_status") ? (
            <Field label="Escalation status">
              <Select
                value={filters.escalation_status}
                onChange={(value) => setFilter("escalation_status", value)}
                options={options.escalation_statuses || []}
              />
            </Field>
          ) : null}
          {enabled.includes("search") ? (
            <Field label="Search">
              <input
                type="search"
                value={filters.search}
                onChange={(e) => setFilter("search", e.target.value)}
                placeholder="Customer, case, account…"
                className={inputClass()}
              />
            </Field>
          ) : null}
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Button type="submit" size="sm">
            Apply filters
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={resetFilters}>
            Reset
          </Button>
          {activeFilterCount > 0 ? (
            <span className="text-[11px] font-semibold text-primary/45">
              {activeFilterCount} filter{activeFilterCount === 1 ? "" : "s"} applied
            </span>
          ) : null}
          <span className="ml-auto inline-flex flex-wrap items-center gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => handleExport("Excel")}>
              <Download className="mr-1.5 h-3.5 w-3.5" />
              Excel
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={() => handleExport("CSV")}>
              CSV
            </Button>
          </span>
        </div>
      </form>

      <div className="overflow-hidden rounded-xl border border-primary/[0.07] bg-white shadow-[0_1px_3px_rgba(127,29,29,0.06)]">
        {loading && rows.length === 0 && columns.length === 0 ? (
          <p className="px-5 py-16 text-center text-sm text-primary/45">Loading report…</p>
        ) : columns.length === 0 && rows.length === 0 ? (
          <div className="px-6 py-16 text-center">
            <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/[0.05] text-primary/30">
              <Inbox className="h-6 w-6" />
            </span>
            <p className="mt-3 text-sm font-medium text-primary/50">No rows for this report yet.</p>
          </div>
        ) : (
          <DataTable
            key={`${slug}-${page}`}
            resetKey={`${slug}-${page}`}
            embedded
            paginate={false}
            compact
            maxVisibleColumns={7}
            columns={columns.map((column) => ({
              key: column.key,
              header: column.label,
              wide: column.key === "ageing_distribution",
              render: (row) => {
                const value = row[column.key];
                const text = value == null ? "" : String(value).trim();
                if (!text || text === MISSING || /^customer$/i.test(text)) {
                  return <span className="text-primary/35">—</span>;
                }
                if (column.key === "ageing_distribution") return <AgeingBrackets value={text} />;
                return formatCell(value);
              },
            }))}
            data={rows}
            emptyMessage="No rows for these filters."
            getRowId={(row, index) => `${slug}-${page}-${index}`}
          />
        )}
        {pagination.last_page > 1 ? (
          <ListPagination
            page={page}
            totalPages={pagination.last_page}
            totalItems={pagination.total}
            startIndex={pagination.from || 0}
            endIndex={pagination.to || 0}
            goToPage={(next) => load(next)}
            alwaysShow
            pageSize={25}
          />
        ) : null}
      </div>
    </PageShell>
  );
}
